import React from 'react'

import { Controller, useForm } from 'react-hook-form'
import { HiOutlineInformationCircle } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Warning } from '@/components/callout/Warning'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/common/Dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import FileUpload, { LocalFile } from '@/components/files/FileUpload'
import VisibilityToggle from '@/components/permissions/PermissionsForm/VisibilityToggle'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import {
  type UploadWordTemplatesMutationArgs,
  useUploadWordTemplates,
} from '@/hooks/word-templates/useUploadWordTemplates'

import type { UUID } from '@/types/Id'

const MICROSOFT_STYLES_DOCS_URL =
  'https://support.microsoft.com/en-us/office/customize-or-create-new-styles-d38d6e47-f6fc-48eb-a607-1eb120dec563#ID0EFH'

interface UploadFileFormData {
  file: File | null
  tag_ids: UUID[]
  cover_pages: number
  unrestricted: boolean
}

interface UseUploadTemplateDialogProps {
  open: boolean
  initialValues?: Partial<UploadFileFormData>
  setOpen: (open: boolean) => void
}

export const UploadTemplateDialog: React.FC<UseUploadTemplateDialogProps> = ({
  open,
  initialValues,
  setOpen,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutateAsync: uploadTemplates, isPending } = useUploadWordTemplates({
    orgId: activeOrgId,
  })

  const { control, handleSubmit, reset, setValue, watch } =
    useForm<UploadFileFormData>({
      defaultValues: {
        file: null,
        tag_ids: [],
        cover_pages: 0,
        unrestricted: true,
        ...initialValues,
      },
    })

  const selectedFile = watch('file')
  const coverPageDisabled = isPending || selectedFile === null

  const onSubmit = async (data: UploadFileFormData) => {
    if (!data.file) {
      return
    }
    try {
      let req: UploadWordTemplatesMutationArgs = {
        templates: [
          {
            file: data.file,
            tag_ids: data.tag_ids,
            cover_pages: data.cover_pages,
            unrestricted: data.unrestricted,
          },
        ],
      }
      await uploadTemplates(req)
      setOpen(false)
      reset({ file: null, tag_ids: [], cover_pages: 0 })
    } catch (error) {
      console.error(error)
    }
  }

  const handleCancel = () => {
    reset({ file: null, tag_ids: [], cover_pages: 0 })
    setOpen(false)
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      reset({ file: null, tag_ids: [], cover_pages: 0, unrestricted: true })
    }
    setOpen(open)
  }

  const handleAddFiles = (files: File[]) => {
    setValue('file', files[0])
  }

  const handleDeleteFile = () => {
    setValue('file', null)
    setValue('cover_pages', 0)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="md:min-w-[756px] max-w-[95%] md:max-w-[756px] p-6"
      >
        <div className="flex flex-col gap-4">
          <DialogTitle>Upload A Word Template</DialogTitle>
          <div className="flex flex-col gap-4">
            <DialogDescription>
              Upload a Word template to use when exporting content written on
              Sweetspot to Microsoft Word.
            </DialogDescription>
            <Warning>
              Any content in the template will be overwritten during generation,
              except for the cover page (if indicated below).
            </Warning>
            <Warning>
              <span>
                Word styles must be properly defined in the template file.
                <a
                  href={MICROSOFT_STYLES_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
                >
                  Learn how to create and update styles.
                </a>
              </span>
            </Warning>
          </div>
          <div className="h-[1px] w-full bg-primary-200 dark:bg-primary-800" />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              control={control}
              name="file"
              render={({ field }) => {
                const { ref, ...rest } = field
                return (
                  <div className="flex flex-col gap-2">
                    <FileUpload
                      {...rest}
                      onAddFiles={handleAddFiles}
                      accepts={[
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                      ]}
                      className="py-6"
                    />
                    {field.value && (
                      <div className="flex flex-col gap-2 pt-2">
                        <LocalFile
                          file={field.value}
                          onDelete={handleDeleteFile}
                        />
                      </div>
                    )}
                  </div>
                )
              }}
            />
            <Controller
              control={control}
              name="unrestricted"
              defaultValue={true}
              render={({ field: { value, onChange } }) => (
                <VisibilityToggle
                  isUnrestricted={value ?? true}
                  onChange={onChange}
                  resourceType="word template"
                  parentResourceType="folder or organization"
                  isNew
                />
              )}
            />
            <DialogFooter className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="cover_pages"
                  render={({ field }) => {
                    const checked = field.value > 0
                    const onChange = () => {
                      field.onChange(checked ? 0 : 1)
                    }
                    return (
                      <Checkbox
                        id="cover_pages"
                        checked={checked}
                        onClick={onChange}
                        disabled={coverPageDisabled}
                        childrenOnly={true}
                        className={twMerge(
                          coverPageDisabled && 'cursor-not-allowed',
                        )}
                      />
                    )
                  }}
                />
                <label
                  htmlFor="cover_pages"
                  className="inline-flex items-center gap-2 select-none text-sm text-primary-700 dark:text-primary-300"
                >
                  Check this box if the template includes a cover page
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <HiOutlineInformationCircle
                        size={16}
                        className="flex-shrink-0"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px]">
                      <span>
                        If this template includes a cover page, selecting this
                        option will keep the cover page in the exported
                        document. Otherwise, the cover page will be removed any
                        time this template is used when exporting a document.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline-compact"
                  disabled={isPending}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary-compact"
                  isLoading={isPending}
                  disabled={isPending || selectedFile === null}
                >
                  Upload
                </Button>
              </div>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
